// legacyPaths — the old paths that still arrive, and where they go now.
//
// ── Why this exists ─────────────────────────────────────────────────────────
//
// The SERVER writes in-app paths into `notifications.link` (server/database.js
// :412) and the Notifications screen renders each as an "Open" link. Those
// literals were written for the classic app and nothing has rewritten them:
//
//   '/timesheets'   routes/timesheets.js  (submit, create-for-worker, and
//                                          every approve/reject)
//                   routes/notifications.js POST /timesheet-submitted
//   '/jobs'         routes/notifications.js POST /job-paused
//   '/dispatch'     routes/field.js       (problem report, job claimed, a
//                                          clock-out carrying a reason)
//
// `/jobs` still exists. `/timesheets` and `/dispatch` do not: they were
// classic routes, and `/dispatch` is now a SERVER router (CLAUDE.md — a path
// names a capability; /field, /dispatch and /office remain server routers).
// RebuildRouter used to carry a fifteen-entry `OLD_PATHS_TO_APP` that
// redirected each of them, and it was deleted with the classic app on
// 2026-09-05 (d25258b). Nothing replaced it, so since that day tapping "Open"
// on a timesheet-approved notification has landed on "No such screen" — on
// every approve, every reject and every crew problem report.
//
// Old rows do not expire either. `notifications` has no retention sweep, so
// inboxes still hold links written when '/billing', '/workers', '/profile'
// and '/time-tracker' were real screens.
//
// ── Where each one goes, and the evidence ───────────────────────────────────
//
// Read off the deleted code and docs/REBUILD_SPEC.md §10, which records the
// old→new decision for every classic route. Not guessed:
//
//   /time-tracker   FieldApp's index route was <Today />          (d25258b^)
//   /field          same file, same index route                   (d25258b^)
//   /dispatch       DispatchApp's index route was <Now />         (d25258b^)
//   /office         OfficeApp's index route was <Overview />      (d25258b^)
//   /timesheets     §10 "Dispatch — Board and Approve"
//   /billing        §10 "one screen each" — Invoices
//   /workers        §10 "one screen each" — the Crew record screen
//   /admin          §10 "Office → Users"
//   /profile        §10 "retired: theme, version and log out are on Me"
//   /paystubs       classic App.js already answered these with
//   /my-paystubs      <Navigate to="/payroll">; §10 adds "Field → My hours"
//   /activity-log   the Activity log screen, renamed to /activity
//   /communication  §10 files this under Reports, and that is WRONG: the
//                   component (components/Communication/Communication.js in
//                   d25258b^) is a GET /api/admin/email-log viewer, header
//                   "v2.9.35 email send-log viewer". That is /email-log.
//
// `/ai-assistant` is deliberately absent. §10 retired it with nothing behind
// it, and a redirect to an unrelated screen would be a worse answer than the
// not-found screen's sentence. Retired means retired.
//
// ── Two candidates, where two roles receive the same link ───────────────────
//
// `to` is a list in preference order, not a single path, because three of
// these rows genuinely land in two different places depending on who is
// holding the phone — and the server sends the SAME string to both:
//
//   '/timesheets' goes to managers when a sheet is submitted, and to the
//   WORKER when it is approved or rejected. /approve is not a screen a crew
//   member's role offers, so resolving there would replace "No such screen"
//   with "Not part of your app" — a refusal for the person the notification
//   was addressed to. /hours is their sheet.
//
// A caller that passes no `reachable` gets the first candidate, so the plain
// one-argument form stays a pure path→path lookup.

/**
 * from  the old path. Matched exactly or as a prefix (`/timesheets/7`).
 * to    current paths, best first. Later entries are used only when the
 *       person cannot reach an earlier one.
 */
export const LEGACY_PATHS = [
  { from: '/time-tracker', to: ['/today'] },
  { from: '/timesheets', to: ['/approve', '/hours'] },
  { from: '/billing', to: ['/invoices'] },
  { from: '/workers', to: ['/crew'] },
  { from: '/admin', to: ['/users'] },
  { from: '/activity-log', to: ['/activity'] },
  { from: '/communication', to: ['/email-log'] },
  { from: '/profile', to: ['/me'] },
  { from: '/paystubs', to: ['/payroll', '/hours'] },
  { from: '/my-paystubs', to: ['/payroll', '/hours'] },
  // The three app prefixes. They were route trees; they are server routers
  // now, and a bookmark or a notification carrying one reaches no screen.
  { from: '/field', to: ['/today'] },
  { from: '/dispatch', to: ['/now'] },
  { from: '/office', to: ['/overview'] },
];

/** '/timesheets?week=1' → { pathname: '/timesheets', tail: '?week=1' }. */
function split(raw) {
  const cut = raw.search(/[?#]/);
  return cut === -1
    ? { pathname: raw, tail: '' }
    : { pathname: raw.slice(0, cut), tail: raw.slice(cut) };
}

/**
 * The table row a pathname belongs to, or null.
 *
 * Longest match wins, the same rule navigation.js:keyForPath uses, so a future
 * row for a sub-path of an existing one cannot be shadowed by its parent.
 */
export function legacyEntryFor(pathname) {
  const p = String(pathname || '');
  let best = null;
  for (const e of LEGACY_PATHS) {
    if (p === e.from || p.startsWith(`${e.from}/`)) {
      if (!best || e.from.length > best.from.length) best = e;
    }
  }
  return best;
}

/**
 * Where a link should actually go.
 *
 *   resolveLegacyPath('/timesheets')            → '/approve'
 *   resolveLegacyPath('/timesheets?week=x')     → '/approve?week=x'
 *   resolveLegacyPath('/timesheets/7')          → '/approve'
 *   resolveLegacyPath('/hours')                 → '/hours'      (untouched)
 *   resolveLegacyPath('//evil.example')         → null
 *
 * `opts.reachable(path)` is asked, when given, which candidates this person
 * can actually open; the first it says yes to wins, and the first candidate is
 * the fallback if it says no to all of them (a refusal on the right screen
 * beats a redirect to a screen that is not even the subject).
 *
 * ── Trailing segments are DROPPED, on purpose ──────────────────────────────
 *
 * `/timesheets/7` resolves to `/approve`, not `/approve/7`. Every classic
 * route in the table was flat — App.js in d25258b^ declares no child route
 * under any of them — so a trailing segment is either junk or an id from some
 * other numbering. `/approve/:id` is a TIMESHEET id and `/invoices/:id` an
 * invoice id; carrying a stranger's number into one of those opens a real
 * record that is the wrong record, which is the failure CLAUDE.md calls the
 * worst outcome available here. Landing one screen up is recoverable.
 *
 * Anything that is not an in-app path returns null rather than passing
 * through: '//host' is protocol-relative and '/\host' is treated that way by
 * some browsers, which is the check routes/notifications.js applies to the one
 * link field a person can type.
 */
export function resolveLegacyPath(link, opts = {}) {
  const raw = typeof link === 'string' ? link.trim() : '';
  if (!raw.startsWith('/')) return null;
  if (raw.startsWith('//') || raw.startsWith('/\\')) return null;
  const { pathname, tail } = split(raw);
  const entry = legacyEntryFor(pathname);
  if (!entry) return raw;
  const reachable = typeof opts.reachable === 'function' ? opts.reachable : null;
  const to = (reachable && entry.to.find(reachable)) || entry.to[0];
  return `${to}${tail}`;
}

/** true when this path is one the app has stopped serving. */
export function isLegacyPath(pathname) {
  return !!legacyEntryFor(pathname);
}
